import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { DateRange } from '@factorialco/f0-react/dist/experimental'
import { IrisValueObjectsFormatHintEnum } from 'generated/resources/datatable.graphql'
import { DataCatalogQuery } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'
import { getRenderedDefFromTableId } from 'modules/ai_reports/components/DatasourceTypeRenderer/tableRendererRegistry'
import { datasourceTypeFromId } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'
import { DateNavigationFilter } from 'modules/ai_reports/components/DateNavigationFilter'
import { Layout } from 'modules/ai_reports/components/Layout'
import { ReportWidget } from 'modules/ai_reports/components/ReportWidget/ReportWidget'
import { SaveModal } from 'modules/ai_reports/components/SaveModal'
import { assertDefined } from 'modules/ai_reports/lib/assertDefined'
import { exportReport } from 'modules/ai_reports/lib/exportReport'
import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'
import { useNavConfirmModal } from 'modules/ai_reports/lib/useNavConfirmModal'
import { useSaveReport } from 'modules/ai_reports/lib/useSaveReport'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export const AiReportsMainContent = ({
  sourceId,
  datasource,
}: {
  sourceId?: string
  datasource?: DataCatalogQuery['iris']['factTablesConnection']['nodes'][number]
}) => {
  const { company } = useCurrentSession()

  assertDefined(sourceId, 'Source ID is required')
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [saveModalOpen, setSaveModalOpen] = useState(false)

  type InitialStateType = {
    type: 'text2sql'
    currentDataset: string
    sqlAnswer: AiReportsResult['sqlAnswer']
  }

  const defaultSqlAnswer: AiReportsResult['sqlAnswer'] = useMemo(
    () => ({
      title: '',
      summary: '',
      query_summary: '',
      description: '',
      column_format_hints: [],
      tables: [],
      resolved_entities_info: [],
      success: false,
      synthetic_sql: false,
      sql: `SELECT * FROM \`${sourceId}\``,
    }),
    [sourceId]
  )

  const { state, setState } = useCocreationAgent<AiReportsResult, InitialStateType>({
    name: 'ai-reports-workflow',
    initialState: {
      type: 'text2sql',
      currentDataset: sourceId,
      sqlAnswer: defaultSqlAnswer,
    },
    initialMessage: datasource?.name?.trim()
      ? i18n.t('ai_reports.ai.initial_message_with_dataset', {
          datasetName: datasource?.name.trim(),
        })
      : i18n.t('ai_reports.ai.initial_message'),
  })

  useAutoOpenChat()

  const queryToExecute = state.sqlAnswer?.sql || defaultSqlAnswer.sql
  const reportName = 'AI Report'

  useEffect(() => {
    if (queryToExecute !== defaultSqlAnswer.sql) {
      setNavigationBlocked(true)
    }
  }, [queryToExecute])

  const canSave = queryToExecute?.length > 0

  useEffect(() => {
    setState((prev) => {
      assertDefined(prev, 'Previous state is undefined')
      return {
        ...prev,
        currentDataset: sourceId,
        sqlAnswer: { ...prev.sqlAnswer, sql: defaultSqlAnswer.sql },
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId])

  const { NavConfirmModal, setNavigationBlocked } = useNavConfirmModal()

  const { saveReport } = useSaveReport({
    onSaveSuccess: (reportId: string) => {
      setNavigationBlocked(false)
      setTimeout(() => {
        navigate(navigation.aiReports.reports.reportId.fullPath({ reportId }))
      }, 100)
    },
  })

  const handleSave = async ({ name, description }: { name: string; description: string }) => {
    if (!canSave) return

    saveReport({
      name: name,
      datasourceId: sourceId,
      description: description,
      widgets: [
        {
          name: 'main-table',
          description: state.sqlAnswer?.description ?? '',
          query: queryToExecute,
          position: {
            x: 0,
            y: 0,
            width: 12,
            height: 6,
          },
        },
      ],
      filters: {
        snapshotDate: state.filters?.snapshotDate?.toISOString(),
        dateRange:
          state.filters?.dateRange?.from && state.filters?.dateRange?.to
            ? {
                from: state.filters.dateRange.from.toISOString(),
                to: state.filters.dateRange.to.toISOString(),
              }
            : undefined,
      },
      columnFormatHints:
        state.sqlAnswer?.column_format_hints.map((hint) => ({
          columnDisplayName: hint.column_alias,
          formatHint: hint.column_format as IrisValueObjectsFormatHintEnum,
        })) || [],
    })
  }

  const handleExport = () => {
    if (!reportName || !queryToExecute || !company.id) return
    exportReport(
      reportName,
      queryToExecute,
      company.id,
      datasourceTypeFromId(sourceId),
      state.filters || {}
    )
  }

  const [dateFilter, setDateFilter] = useState<DateRange | undefined>(undefined)

  const rendererDef = getRenderedDefFromTableId(sourceId)
  const dateNavigatorConfig = rendererDef?.dateNavigatorConfig?.({})

  return (
    <>
      {/* This is a hack to make the page full height and to use the responsive page body to get the header action clicks*/}
      <Box className="hidden">
        <ResponsivePage.Body
          onActionClick={(key: string) => {
            if (key === 'save') {
              setSaveModalOpen(true)
            } else if (key === 'export') {
              handleExport()
            }
          }}
          variant="fullscreen"
        >
          &nbsp;
        </ResponsivePage.Body>
      </Box>
      <NavConfirmModal />
      <SaveModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onClickSave={handleSave}
        name={state.sqlAnswer?.title}
        description={state.sqlAnswer?.query_summary}
      />
      <Layout
        header={
          dateNavigatorConfig && (
            <DateNavigationFilter
              config={dateNavigatorConfig}
              onSelect={(value) => {
                setDateFilter(value?.value)
              }}
            />
          )
        }
      >
        <ReportWidget
          sqlQuery={queryToExecute}
          description={state.sqlAnswer?.description ?? ''}
          dateFilter={dateFilter}
          tableId={sourceId ?? ''}
          columnFormatHints={state.sqlAnswer?.column_format_hints || []}
          reportName={reportName}
          onChangeFilters={(filters) =>
            setState((prev) => {
              assertDefined(prev, 'Previous state is undefined')
              return { ...prev, filters }
            })
          }
          hideMetadata={queryToExecute === defaultSqlAnswer.sql}
        />
      </Layout>
    </>
  )
}
